import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getDashboard, getAnalytics } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { Metric } from '@/components/ui/metric';
import { SectionRule } from '@/components/ui/section-rule';
import { StatusDot } from '@/components/ui/status-dot';
import { EmptyState } from '@/components/ui/empty-state';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Plus, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount || 0);
};

/* Charts read the single-hue ramp from index.css — no rainbow. */
const RAMP = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))'];
const AXIS_TICK = { fontSize: 11, fill: 'hsl(var(--ink-subtle))' };
const TOOLTIP_STYLE = {
  background: 'hsl(var(--surface))',
  border: '1px solid hsl(var(--line))',
  borderRadius: '6px',
  boxShadow: 'none',
  fontSize: '13px',
};

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, analyticsRes] = await Promise.all([getDashboard(), getAnalytics()]);
      setData(dashRes.data);
      setAnalytics(analyticsRes.data);
    } catch {}
    setLoading(false);
    setLastRefresh(Date.now());
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return <div className="space-y-6"><div className="h-8 w-48 bg-wash rounded-md animate-pulse" /><div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">{[1,2,3,4].map(i => <div key={i} className="h-32 bg-wash rounded-md animate-pulse" />)}</div></div>;
  }

  const stats = [
    { label: 'Total revenue', value: formatCurrency(data?.total_revenue) },
    { label: 'Pending invoices', value: data?.pending_invoices || 0 },
    { label: 'Proposal win rate', value: `${data?.win_rate || 0}%` },
    { label: 'Active projects', value: data?.active_projects || 0 },
  ];

  // Personalized greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };
  const firstName = (user?.name || 'there').split(' ')[0];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <PageHeader
        title={`${getGreeting()}, ${firstName}`}
        description="Your freelance business at a glance"
        actions={
          <>
            <Button variant="ghost" size="icon" onClick={fetchData} data-testid="refresh-btn" title="Refresh">
              <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
            </Button>
            <Button onClick={() => navigate('/proposals/new')} data-testid="quick-new-proposal">
              <Plus className="w-4 h-4" strokeWidth={1.5} /> New proposal
            </Button>
            <Button variant="outline" onClick={() => navigate('/invoices/new')} data-testid="quick-new-invoice">
              New invoice
            </Button>
            <Button variant="outline" onClick={() => navigate('/clients')} data-testid="quick-new-client">
              Clients
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((s, i) => (
          <Metric key={i} label={s.label} value={s.value} data-testid={`stat-card-${i}`} />
        ))}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-8">
          <section data-testid="recent-activity">
            <SectionRule label="Recent activity" />
            {(!data?.recent_activity || data.recent_activity.length === 0) ? (
              <EmptyState
                title="No activity yet"
                description="Create your first proposal or invoice to get started."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.recent_activity.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-ink">{item.title}</TableCell>
                      <TableCell className="text-ink-muted capitalize">{item.type}</TableCell>
                      <TableCell className="text-ink-muted">{new Date(item.date).toLocaleDateString()}</TableCell>
                      <TableCell><StatusDot status={item.status} /></TableCell>
                      <TableCell className="text-right text-ink">{formatCurrency(item.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </section>
        </TabsContent>

        <TabsContent value="analytics" className="mt-8 space-y-12">
          {/* Revenue Chart */}
          <section data-testid="revenue-chart">
            <SectionRule label="Revenue trend" />
            {analytics?.monthly_revenue?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={analytics.monthly_revenue} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={RAMP[0]} stopOpacity={0.14} />
                      <stop offset="95%" stopColor={RAMP[0]} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--line))" vertical={false} />
                  <XAxis dataKey="month" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                  <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                  <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, 'Revenue']} contentStyle={TOOLTIP_STYLE} />
                  <Area type="monotone" dataKey="revenue" stroke={RAMP[0]} strokeWidth={2} fill="url(#revenueGradient)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState title="No revenue data yet" description="Complete your first invoice to see trends." />
            )}
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Proposals Breakdown */}
            <section data-testid="proposals-chart">
              <SectionRule label="Proposals by status" />
              {analytics?.proposals_breakdown?.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={analytics.proposals_breakdown} cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={3} dataKey="value">
                        {analytics.proposals_breakdown.map((_, i) => (
                          <Cell key={i} fill={RAMP[i % RAMP.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={TOOLTIP_STYLE} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-3 flex flex-wrap justify-center gap-4">
                    {analytics.proposals_breakdown.map((item, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-small text-ink-muted">
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: RAMP[i % RAMP.length] }} />
                        {item.name}: <span className="tabular-nums">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <EmptyState title="No proposals yet" />
              )}
            </section>

            {/* Top Clients */}
            <section data-testid="top-clients-chart">
              <SectionRule label="Top clients by revenue" />
              {analytics?.top_clients?.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={analytics.top_clients} layout="vertical" margin={{ left: 10, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--line))" horizontal={false} />
                    <XAxis type="number" tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                    <YAxis type="category" dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} width={80} />
                    <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, 'Revenue']} contentStyle={TOOLTIP_STYLE} />
                    <Bar dataKey="revenue" fill={RAMP[0]} radius={[0, 3, 3, 0]} barSize={20} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState title="No client revenue data yet" />
              )}
            </section>
          </div>

          {/* Hours Tracked */}
          <section data-testid="hours-chart">
            <SectionRule
              label="Hours tracked"
              action={<span className="text-small text-ink-muted tabular-nums">{analytics?.total_hours || 0} total</span>}
            />
            {analytics?.monthly_hours?.some(h => h.hours > 0) ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={analytics.monthly_hours} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--line))" vertical={false} />
                  <XAxis dataKey="month" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                  <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v) => [`${v}h`, 'Hours']} contentStyle={TOOLTIP_STYLE} />
                  <Bar dataKey="hours" fill={RAMP[1]} radius={[3, 3, 0, 0]} barSize={32} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState title="No time entries yet" description="Start tracking hours on your projects." />
            )}
          </section>
        </TabsContent>
      </Tabs>
    </div>
  );
}
